import React, { useState, useEffect } from 'react';
import { AlertCircle, Mail, X, Check } from 'lucide-react';
import { auth, supabase } from '../../lib/supabase';
import { log } from '../../utils/logger';

const EmailConfirmationBanner = ({ user, onLogout }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isTeamRezyEmployee, setIsTeamRezyEmployee] = useState(false);

  useEffect(() => {
    // Check if user exists and if email is confirmed
    if (user && !user.email_confirmed_at) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }

    // Check if the current user is a TeamRezy employee
    if (user && user.role === 'teamrezy_employee') {
      setIsTeamRezyEmployee(true);
    } else {
      setIsTeamRezyEmployee(false);
    }
  }, [user]);

  const handleManualConfirmation = async () => {
    if (!user || !user.email) return;
    
    setIsLoading(true);
    setMessage('');
    
    try {
      // For TeamRezy employees, we'll mark the user as active in our database
      // This doesn't actually confirm the email in Supabase Auth, but it allows the user to use the system
      const { error } = await supabase
        .from('users')
        .update({ is_active: true })
        .eq('id', user.id);
      
      if (error) throw error;
      
      // Hide the banner
      setIsVisible(false);
      setMessage('User has been manually activated.');
    } catch (err) {
      // Error caught and ignored
      log.error('Error manually confirming user:', err);
      setMessage(`Failed to manually confirm user: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!user || !user.email) return;
    
    setIsLoading(true);
    setMessage('');
    
    try {
      // Use Supabase to resend the confirmation email
      const { error } = await auth.resetPasswordForEmail(user.email);
      
      if (error) throw error;
      
      setMessage('Confirmation email has been resent. Please check your inbox.');
    } catch (err) {
      // Error caught and ignored
      log.error('Error resending confirmation email:', err);
      setMessage(`Failed to resend email: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gold-50 border-l-4 border-gold-400 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <div className="text-sm text-yellow-700">
            <p className="font-medium">Email verification required</p>
            <p className="mt-1">
              Your email address has not been verified. Some features may be limited until you verify your email.
            </p>
            {message && (
              <p className={`mt-2 ${message.includes('resent') ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </p>
            )}
            <div className="mt-3 flex space-x-4">
              <button
                onClick={handleResendEmail}
                disabled={isLoading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gold-800 bg-gold-100 hover:bg-gold-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-gold-800 rounded-full"></span>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Mail className="mr-1.5 h-4 w-4" />
                    Resend verification email
                  </span>
                )}
              </button>
              
              {/* Manual confirmation button for TeamRezy employees */}
              {isTeamRezyEmployee && (
                <button
                  onClick={handleManualConfirmation}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-green-700 rounded-full"></span>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Check className="mr-1.5 h-4 w-4" />
                      Manually Activate User
                    </span>
                  )}
                </button>
              )}
              
              <button
                onClick={onLogout}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              onClick={() => setIsVisible(false)}
              className="inline-flex bg-gold-50 rounded-md p-1.5 text-gold-600 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmationBanner;
